import { useState, useEffect } from 'react';
import { apiRequest } from '../api/client';
import { EMPTY_LOGIN, EMPTY_REG } from '../constants/forms';
import {
  parseIneMetadata,
  validateCurp,
  validateIneKeyStructure,
  validateIneNameMatch,
} from '../utils/identityValidators';

export function useAuth(notify) {
  const [token, setToken] = useState(() => localStorage.getItem('ca_token') || '');
  const [user,  setUser]  = useState(null);
  const [authMode, setAuthModeState]  = useState('login');
  const [loginForm, setLoginForm]     = useState(EMPTY_LOGIN);
  const [regForm,   setRegForm]       = useState(EMPTY_REG);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetForm, setResetForm]     = useState({ token: '', newPassword: '' });
  const [showPassword, setShowPassword]     = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);
  const [hcaptchaSiteKey, setHcaptchaSiteKey] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [hcaptchaToken, setHcaptchaToken] = useState('');
  const [captchaResetCounter, setCaptchaResetCounter] = useState(0);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [states, setStates]                 = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [cees, setCees] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [municipalitiesLoading, setMunicipalitiesLoading] = useState(false);

  function resetAuthForms() {
    setLoginForm(EMPTY_LOGIN);
    setRegForm(EMPTY_REG);
    setForgotEmail('');
    setResetForm({ token: '', newPassword: '' });
    setShowPassword(false);
    setShowRegisterPassword(false);
    setShowRegisterConfirmPassword(false);
    setHcaptchaToken('');
    setCaptchaResetCounter((prev) => prev + 1);
  }

  function setAuthMode(nextMode, resetForms = true) {
    if (resetForms) {
      resetAuthForms();
    }
    setAuthModeState(nextMode);
  }

  useEffect(() => {
    if (!token) { setUser(null); return; }
    apiRequest('/auth/me', { method: 'GET' }, token)
      .then(setUser)
      .catch(() => { localStorage.removeItem('ca_token'); setToken(''); });
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const confirmToken = params.get('token');
    if (!confirmToken) return;

    let active = true;
    apiRequest(`/auth/confirm-account?token=${encodeURIComponent(confirmToken)}`, { method: 'GET' })
      .then((data) => {
        if (!active) return;
        notify('success', data?.message || 'Cuenta confirmada correctamente. Ya puedes iniciar sesión.');
        setAuthMode('login', false);
      })
      .catch((err) => {
        if (!active) return;
        notify('error', err.message || 'No se pudo confirmar la cuenta.');
        setAuthMode('login', false);
      })
      .finally(() => {
        params.delete('token');
        const query = params.toString();
        const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', nextUrl);
      });

    return () => {
      active = false;
    };
  }, [notify]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;
    setCatalogLoading(true);

    apiRequest('/catalog/states', { method: 'GET' })
      .then((data) => {
        if (!mounted) return;
        setStates(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setStates([]);
        notify('error', err.message || 'No se pudo cargar el catalogo de estados.');
      })
      .finally(() => {
        if (mounted) setCatalogLoading(false);
      });

    // load CEEs catalog (accept either raw array or { data: [] })
    // Note: API_BASE already includes /candidate-assemblies, so request path is /catalog/cees
    apiRequest('/catalog/cees', { method: 'GET' })
      .then((data) => {
        if (!mounted) return;
        const items = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setCees(items);
      })
      .catch(() => {
        if (!mounted) return;
        setCees([]);
      });

    return () => { mounted = false; };
  }, [notify]);

  useEffect(() => {
    let mounted = true;
    const stateId = Number(regForm.stateId);
    if (!Number.isInteger(stateId) || stateId < 1) {
      setMunicipalities([]);
      return () => { mounted = false; };
    }

    setMunicipalitiesLoading(true);
    apiRequest(`/catalog/municipalities?entidad=${encodeURIComponent(String(stateId))}`, { method: 'GET' })
      .then((data) => {
        if (!mounted) return;
        setMunicipalities(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setMunicipalities([]);
        notify('error', err.message || 'No se pudo cargar el catalogo de municipios.');
      })
      .finally(() => {
        if (mounted) setMunicipalitiesLoading(false);
      });

    return () => { mounted = false; };
  }, [regForm.stateId, notify]);

  async function refreshCaptcha() {
    setHcaptchaToken('');
    setCaptchaResetCounter((prev) => prev + 1);
  }

  function handleCaptchaVerify(tokenValue) {
    setHcaptchaToken(String(tokenValue || ''));
  }

  function handleCaptchaExpire() {
    setHcaptchaToken('');
  }

  function handleCaptchaError() {
    setHcaptchaToken('');
    notify('error', 'No se pudo validar hCaptcha. Intenta recargarlo.');
  }

  async function loadCaptchaConfig() {
    setCaptchaLoading(true);
    try {
      const data = await apiRequest('/auth/captcha-config', { method: 'GET' });
      const siteKey = String(data?.siteKey || '').trim();
      const enabled = Boolean(data?.enabled) && Boolean(siteKey);
      setHcaptchaSiteKey(siteKey);
      setCaptchaEnabled(enabled);
      setHcaptchaToken('');
      setCaptchaResetCounter((prev) => prev + 1);
    } catch (err) {
      setHcaptchaSiteKey('');
      setCaptchaEnabled(false);
      setHcaptchaToken('');
      notify('error', err.message || 'No se pudo cargar configuracion de hCaptcha.');
    } finally {
      setCaptchaLoading(false);
    }
  }

  useEffect(() => {
    if (authMode === 'register') {
      loadCaptchaConfig();
    }
  }, [authMode]); // eslint-disable-line react-hooks/exhaustive-deps

  function getPasswordValidationMessage(password) {
    if (!password || password.length < 10) {
      return 'La contrasena debe tener al menos 10 caracteres.';
    }
    if (!/[A-Z]/.test(password)) {
      return 'La contrasena debe incluir al menos una letra mayuscula.';
    }
    if (!/[a-z]/.test(password)) {
      return 'La contrasena debe incluir al menos una letra minuscula.';
    }
    if (!/\d/.test(password)) {
      return 'La contrasena debe incluir al menos un numero.';
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'La contrasena debe incluir al menos un simbolo.';
    }
    return null;
  }

  async function handleLogin(e) {
    e.preventDefault();
    try {
      const res = await apiRequest('/auth/login', { method: 'POST', body: JSON.stringify(loginForm) });
      if (!res?.token || !res?.user) throw new Error('Respuesta de login invalida.');
      localStorage.setItem('ca_token', res.token);
      setToken(res.token);
      setUser(res.user);
    } catch (err) { notify('error', err.message); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    const ineKey = String(regForm.ineKey || '').trim().toUpperCase();
    const curp = String(regForm.curp || '').trim().toUpperCase();
    const firstName = String(regForm.firstName || '').trim();
    const lastNamePaternal = String(regForm.lastNamePaternal || '').trim();
    const lastNameMaternal = String(regForm.lastNameMaternal || '').trim();
    const federalDistrictRaw = String(regForm.federalDistrict || '').trim();
    const password = String(regForm.password || '');
    const confirmPassword = String(regForm.confirmPassword || '');

    if (!ineKey) {
      notify('error', 'La INE (clave de elector) es obligatoria.');
      return;
    }

    if (!validateIneKeyStructure(ineKey)) {
      notify('error', 'La clave INE no cumple con la estructura valida.');
      return;
    }

    const ineNameMatch = validateIneNameMatch(ineKey, firstName, lastNamePaternal, lastNameMaternal);
    if (ineNameMatch === false) {
      notify('error', 'El nombre y apellidos no coinciden con la clave INE.');
      return;
    }

    const ineMeta = parseIneMetadata(ineKey);
    const curpValidation = validateCurp(curp, {
      name: firstName,
      firstLastName: lastNamePaternal,
      secondLastName: lastNameMaternal,
      birthDate: ineMeta.birthDate,
      sex: ineMeta.sex,
    });

    if (!curpValidation.valid) {
      notify('error', curpValidation.reason || 'La CURP es invalida.');
      return;
    }

    if (!/^\d+$/.test(federalDistrictRaw)) {
      notify('error', 'El Distrito Federal debe ser un entero entre 1 y 40.');
      return;
    }
    const federalDistrict = Number(federalDistrictRaw);
    if (!Number.isInteger(federalDistrict) || federalDistrict < 1 || federalDistrict > 40) {
      notify('error', 'El Distrito Federal debe ser un entero entre 1 y 40.');
      return;
    }

    if (captchaEnabled && hcaptchaSiteKey && !hcaptchaToken) {
      notify('error', 'Resuelve el hCaptcha para continuar.');
      return;
    }

    const passwordError = getPasswordValidationMessage(password);
    if (passwordError) {
      notify('error', passwordError);
      return;
    }

    if (password !== confirmPassword) {
      notify('error', 'La confirmacion de contrasena no coincide.');
      return;
    }

    const normalizedForm = {
      ...regForm,
      ineKey,
      curp,
      firstName,
      lastNamePaternal,
      lastNameMaternal,
      federalDistrict,
      hcaptchaToken: captchaEnabled ? hcaptchaToken : '',
      password,
      hasCee: Boolean(regForm.hasCee),
      cee: String(regForm.cee || '').trim(),
    };

    try {
      const res = await apiRequest('/auth/register', { method: 'POST', body: JSON.stringify(normalizedForm) });
      if (res?.email?.sent === false) {
        notify('error', res?.email?.message || 'Cuenta creada, pero no se pudo enviar el correo de confirmacion.');
      } else {
        notify('success', 'Cuenta creada. Revisa tu correo para confirmar tu cuenta antes de iniciar sesion.');
      }
      setHcaptchaToken('');
      setCaptchaResetCounter((prev) => prev + 1);
      setAuthMode('login');
    } catch (err) {
      notify('error', err.message);
      await refreshCaptcha();
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    try {
      await apiRequest('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email: forgotEmail }) });
      notify('success', 'Si el correo existe, se envio un email con instrucciones para recuperar tu contrasena.');
    } catch (err) { notify('error', err.message); }
  }

  async function handleReset(e) {
    e.preventDefault();
    try {
      await apiRequest('/auth/reset-password', { method: 'POST', body: JSON.stringify(resetForm) });
      setAuthMode('login');
      notify('success', 'Contraseña actualizada. Inicia sesión.');
    } catch (err) { notify('error', err.message); }
  }

  function logout(onCleanup) {
    localStorage.removeItem('ca_token');
    setToken('');
    setUser(null);
    setAuthMode('login');
    if (typeof onCleanup === 'function') onCleanup();
  }

  return {
    token, user,
    authMode, setAuthMode,
    loginForm, setLoginForm,
    regForm, setRegForm,
    forgotEmail, setForgotEmail,
    resetForm, setResetForm,
    showPassword, setShowPassword,
    showRegisterPassword, setShowRegisterPassword,
    showRegisterConfirmPassword, setShowRegisterConfirmPassword,
    hcaptchaSiteKey, captchaEnabled, hcaptchaToken, captchaResetCounter,
    captchaLoading, refreshCaptcha, handleCaptchaVerify, handleCaptchaExpire, handleCaptchaError,
    states, municipalities,
    cees,
    catalogLoading, municipalitiesLoading,
    handleLogin, handleRegister, handleForgot, handleReset,
    logout,
  };
}
